import React from "react";
import { Card, Typography, Button } from "antd";
import { Check } from "lucide-react";
import { motion } from "framer-motion";
import { useInView } from "react-intersection-observer";
import { useNavigate } from "react-router-dom";
import { useLanguage } from "../../../context/LanguageContext";
import content from "../../../utils/content";

const { Title, Text } = Typography;

const PricingTier = ({
  title,
  price,
  description,
  features,
  isBestSeller,
  bestSellerText,
  buttonText,
  delay,
}) => {
  const navigate = useNavigate();
  const { language } = useLanguage();

  return (
    <motion.div
      initial={{ opacity: 0, y: 50 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.8, ease: "easeOut", delay }}
      className="relative"
    >
      {isBestSeller && (
        <div className="absolute -right-2 -top-2 rotate-45 bg-[#FF7051] text-white px-8 py-1 transform translate-x-8 text-sm z-10">
          {bestSellerText}
        </div>
      )}
      <Card className="h-[480px] flex flex-col justify-evenly hover:shadow-lg transition-shadow">
        <div className="space-y-6 flex flex-col justify-between">
          <div className="text-center space-y-2">
            <Title level={3} className="!mb-0">
              {title}
            </Title>
            <Text className="block text-gray-500">{description}</Text>
          </div>
          <div className="text-center">
            <Text className="text-4xl font-bold text-[#FF7051]">€{price}</Text>
            <Text className="text-gray-500">/Month</Text>
          </div>
          <div className="text-sm text-orange-600 mt-1 font-medium">
            🎁 START20 –{" "}
            {language === "de"
              ? "20 % Rabatt im ersten Monat"
              : "20% off your first month"}
          </div>

          <ul className="space-y-3">
            {features.map((feature, index) => (
              <li key={index} className="flex items-center gap-2">
                <Check className="w-5 h-5 text-[#FF7051]" />
                <span>{feature}</span>
              </li>
            ))}
          </ul>
          <Button
            type="primary"
            size="large"
            className="w-full mt-6 bg-[#FF7051] hover:!bg-[#FF7051]/90 justify-self-end "
            onClick={() => navigate("/login")}
          >
            {buttonText}
          </Button>
        </div>
      </Card>
    </motion.div>
  );
};

const PricingSection = () => {
  const { language } = useLanguage();
  const t = content[language].pricing;

  const [titleRef, titleInView] = useInView({
    triggerOnce: true,
    threshold: 0.2,
  });
  const [cardsRef, cardsInView] = useInView({
    triggerOnce: true,
    threshold: 0.2,
  });

  const fadeInUp = (delay = 0) => ({
    hidden: { opacity: 0, y: 50 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.8, delay } },
  });

  return (
    <div className="w-full px-4 md:px-6 py-12 md:py-24" id="Pricing">
      {/* Titel Animation */}
      <motion.div
        ref={titleRef}
        initial="hidden"
        animate={titleInView ? "visible" : "hidden"}
        variants={fadeInUp(0)}
        className="text-center space-y-4 mb-28"
      >
        <Title
          level={2}
          className="lg:!text-[5rem] !text-4xl !font-bold tracking-tight lg:!leading-[5rem] py-4 bg-white"
        >
          <span className="text-[#FF7051]">{t.title.highlight}</span>
          {t.title.rest}
        </Title>
      </motion.div>

      {/* Karten Animation */}
      <div
        ref={cardsRef}
        className="grid lg:gap-8 gap-14 md:grid-cols-3 max-w-5xl mx-auto"
      >
        {cardsInView &&
          t.tiers.map((tier, index) => (
            <PricingTier
              key={index}
              title={tier.title}
              price={tier.price}
              description={tier.description}
              features={tier.features}
              isBestSeller={index === 1}
              bestSellerText={t.bestSeller}
              buttonText={t.button}
              delay={0.2 * (index + 1)}
            />
          ))}
      </div>
    </div>
  );
};

export default PricingSection;
